import React, { useState, useEffect } from "react";
import { downloadAndDecryptFile, formatFileSize, getFileCategory } from "./mediaUtils";

export default function MediaBubble({ msg, sharedKeyRef, isMine }) {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const category = getFileCategory(msg.mediaType);

  useEffect(() => {
    // Auto-load images and thumbnails
    if (category === "image" || msg.mediaThumbnail) {
      loadMedia();
    }
  }, [msg.mediaUrl]);

  const loadMedia = async () => {
    if (!msg.mediaUrl || !msg.mediaKey) {
      setError("Missing media data");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Download and decrypt the file
      const decryptedBlob = await downloadAndDecryptFile(
        msg.mediaUrl,
        msg.mediaKey,
        sharedKeyRef.current,
        msg.mediaType
      );
      
      // Create object URL for display
      const url = URL.createObjectURL(decryptedBlob);
      setMediaUrl(url);
      setLoading(false);
    } catch (err) {
      console.error("Failed to load media:", err);
      setError("Failed to load media");
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (mediaUrl) {
      // Already loaded, just download
      const a = document.createElement("a");
      a.href = mediaUrl;
      
      // Extract filename from S3 key (format: media/mobile/timestamp-filename.ext)
      let filename = "download";
      if (msg.mediaUrl) {
        const parts = msg.mediaUrl.split("/");
        const lastPart = parts[parts.length - 1];
        // Remove timestamp prefix (e.g., "1762009927023-Logo3.png" -> "Logo3.png")
        const match = lastPart.match(/^\d+-(.+)$/);
        filename = match ? match[1] : lastPart;
      }
      
      a.download = filename;
      a.click();
      return;
    }

    // Load and download
    try {
      setDownloading(true);
      await loadMedia();
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const handlePlay = () => {
    if (!mediaUrl) {
      loadMedia();
    }
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (mediaUrl) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
  }, [mediaUrl]);

  // Render based on category
  if (category === "image") {
    return (
      <div className="media-bubble" style={{ maxWidth: "300px" }}>
        {loading && (
          <div className="text-center p-4">
            <div className="spinner-border spinner-border-sm" role="status" />
            <div className="small mt-2">Loading image...</div>
          </div>
        )}
        {error && (
          <div className="alert alert-warning mb-0">
            <small>{error}</small>
            <button className="btn btn-sm btn-link" onClick={loadMedia}>
              Retry
            </button>
          </div>
        )}
        {mediaUrl && (
          <img
            src={mediaUrl}
            alt="Shared media"
            className="img-fluid rounded"
            style={{ maxWidth: "100%", cursor: "pointer" }}
            onClick={handleDownload}
          />
        )}
        {msg.message && msg.message !== "[Media]" && (
          <div className="mt-2 small">{msg.message}</div>
        )}
      </div>
    );
  }

  if (category === "video") {
    return (
      <div className="media-bubble" style={{ maxWidth: "300px" }}>
        {!mediaUrl && (
          <div
            className="bg-dark text-white rounded p-4 text-center"
            style={{ cursor: "pointer", minHeight: "150px", display: "flex", flexDirection: "column", justifyContent: "center" }}
            onClick={handlePlay}
          >
            <i className="bi bi-play-circle fs-1"></i>
            <div className="mt-2 small">Click to play video</div>
            {msg.mediaSize && <div className="small text-muted">{formatFileSize(msg.mediaSize)}</div>}
          </div>
        )}
        {loading && mediaUrl === null && (
          <div className="text-center p-4">
            <div className="spinner-border spinner-border-sm" role="status" />
            <div className="small mt-2">Loading video...</div>
          </div>
        )}
        {mediaUrl && (
          <video
            src={mediaUrl}
            controls
            className="rounded"
            style={{ maxWidth: "100%" }}
          />
        )}
        {msg.message && msg.message !== "[Media]" && (
          <div className="mt-2 small">{msg.message}</div>
        )}
      </div>
    );
  }

  if (category === "audio") {
    return (
      <div className="media-bubble d-flex align-items-center gap-2 p-2 border rounded" style={{ minWidth: "250px" }}>
        {!mediaUrl && !loading && (
          <button className="btn btn-sm btn-primary" onClick={handlePlay}>
            <i className="bi bi-play-fill"></i>
          </button>
        )}
        {loading && (
          <div className="spinner-border spinner-border-sm" role="status" />
        )}
        {mediaUrl && (
          <audio src={mediaUrl} controls className="flex-grow-1" style={{ height: "40px" }} />
        )}
        <div className="flex-grow-1">
          <div className="small fw-bold">
            <i className="bi bi-music-note-beamed me-1"></i>
            Audio
          </div>
          {msg.mediaDuration && (
            <div className="small text-muted">{Math.floor(msg.mediaDuration / 60)}:{(msg.mediaDuration % 60).toString().padStart(2, '0')}</div>
          )}
        </div>
      </div>
    );
  }

  // Document/other files
  return (
    <div 
      className="media-bubble d-flex align-items-center gap-3 p-3 border rounded"
      style={{ minWidth: "250px", cursor: "pointer" }}
      onClick={handleDownload}
    >
      <div className={`rounded-circle d-flex align-items-center justify-content-center ${isMine ? 'bg-light' : 'bg-primary bg-opacity-10'}`} 
           style={{ width: "48px", height: "48px" }}>
        <i className="bi bi-file-earmark-text fs-4 text-primary"></i>
      </div>
      <div className="flex-grow-1">
        <div className="small fw-bold text-truncate">
          {msg.message && msg.message !== "[Media]" ? msg.message : "Document"}
        </div>
        {msg.mediaSize && (
          <div className="small text-muted">{formatFileSize(msg.mediaSize)}</div>
        )}
      </div>
      {downloading ? (
        <div className="spinner-border spinner-border-sm" role="status" />
      ) : (
        <i className="bi bi-download"></i>
      )}
    </div>
  );
}
